import React from 'react';
import { startOfMonth, endOfMonth, subMonths, startOfQuarter, endOfQuarter, startOfYear, format } from 'date-fns';

const toISO = (d) => format(d, 'yyyy-MM-dd');

/**
 * Pure date-range resolver for each preset. Exported so pages/tests can
 * compute the same ranges the toolbar would, without re-deriving the logic.
 */
export function computePeriodRange(preset, { fiscalYear, startDate, endDate } = {}) {
  const now = new Date();
  switch (preset) {
    case 'THIS_MONTH':
      return { startDate: toISO(startOfMonth(now)), endDate: toISO(endOfMonth(now)) };
    case 'LAST_MONTH': {
      const last = subMonths(now, 1);
      return { startDate: toISO(startOfMonth(last)), endDate: toISO(endOfMonth(last)) };
    }
    case 'THIS_QUARTER':
      return { startDate: toISO(startOfQuarter(now)), endDate: toISO(endOfQuarter(now)) };
    case 'FY': {
      const year = fiscalYear || now.getFullYear();
      return { startDate: `${year}-01-01`, endDate: `${year}-12-31` };
    }
    case 'CUSTOM':
      return { startDate: startDate || toISO(startOfYear(now)), endDate: endDate || toISO(now) };
    case 'YTD':
    default:
      return { startDate: toISO(startOfYear(now)), endDate: toISO(now) };
  }
}

const presetOptions = (fiscalYear) => [
  { value: 'THIS_MONTH', label: 'This month' },
  { value: 'LAST_MONTH', label: 'Last month' },
  { value: 'THIS_QUARTER', label: 'This quarter' },
  { value: 'YTD', label: 'Year to date' },
  { value: 'FY', label: `FY ${fiscalYear || new Date().getFullYear()}` },
  { value: 'CUSTOM', label: 'Custom' },
];

/**
 * Period preset dropdown. Selecting a preset (other than Custom) immediately
 * resolves and reports the concrete date range via onChange; Custom reveals
 * two date inputs and reports whatever the user picks.
 */
const PeriodSelect = ({ preset = 'YTD', startDate, endDate, fiscalYear, onChange }) => {
  const handlePresetChange = (e) => {
    const nextPreset = e.target.value;
    if (nextPreset === 'CUSTOM') {
      onChange({ preset: nextPreset, startDate, endDate });
      return;
    }
    onChange({ preset: nextPreset, ...computePeriodRange(nextPreset, { fiscalYear }) });
  };

  const handleDateChange = (field) => (e) => {
    onChange({ preset: 'CUSTOM', startDate, endDate, [field]: e.target.value });
  };

  return (
    <div className="flex items-center gap-2">
      <select
        value={preset}
        onChange={handlePresetChange}
        className="border border-ink-line rounded-md px-3 py-2 text-sm bg-white text-ink focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
      >
        {presetOptions(fiscalYear).map((opt) => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>

      {preset === 'CUSTOM' && (
        <>
          <input
            type="date"
            value={startDate || ''}
            onChange={handleDateChange('startDate')}
            className="border border-ink-line rounded-md px-3 py-2 text-sm text-ink focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
          />
          <span className="text-ink-body text-sm">to</span>
          <input
            type="date"
            value={endDate || ''}
            onChange={handleDateChange('endDate')}
            className="border border-ink-line rounded-md px-3 py-2 text-sm text-ink focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
          />
        </>
      )}
    </div>
  );
};

export default PeriodSelect;
